/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { formatDate } from 'sharedUi/dateUtils';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useCanUpdate, useHasPermission } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import { pipelinesService } from '../../services/pipelines.service';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { EmptyState, Metric, PageSkeleton, Rise, SectionLabel, StatusChip } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY } from '../../components/common/ui-kit';
import AuditInfoCard from '../../components/common/audit-info-card';
import PipelineSourcesPanel from './pipeline-sources-panel';
import PipelineRunList from '../pipeline-runs/pipeline-run-list';
import { artifactTone } from './pipeline-status';

const yes = (v?: boolean) => (v ? 'Yes' : 'No');

const PipelineView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'pipeline', basePath: '/pipelines' });

  // `?tab=sources` is how the create form hands over: a pipeline that has just
  // been made has nothing to show but the place to add its documents.
  const [searchParams] = useSearchParams();
  const TAB_INDEX: Record<string, number> = { general: 0, sources: 1, artifacts: 2, runs: 3 };
  const [tab, setTab] = useState(TAB_INDEX[searchParams.get('tab') || ''] ?? 0);
  const runNav = useSecureNavigation({ entity: 'pipeline-run', basePath: '/pipeline-runs' });
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canEdit = useCanUpdate(JOB_NAMES.PIPELINE);
  const canRun = useHasPermission(PERMISSIONS.PIPELINE_RUN_EXECUTE);
  const canRetire = useHasPermission(PERMISSIONS.PIPELINE_ARTIFACT_RETIRE);
  const [retireId, setRetireId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['pipelines', id], queryFn: () => pipelinesService.getById(id!), enabled: isReady && !!id });
  const { data: artifacts = [], refetch: refetchArtifacts } = useQuery({ queryKey: ['pipeline-artifacts', id], queryFn: () => pipelinesService.listArtifacts(id!), enabled: isReady && !!id });
  useBreadcrumbTitle(data?.name);

  const run = useMutation({
    mutationFn: () => pipelinesService.run(id!),
    onSuccess: (r) => { toast.current?.show({ severity: 'success', summary: 'Run started', detail: r.pipelineRunId, life: 4000 }); runNav.navigateToView(r._id); },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Failed to start the run'), life: 10000 }),
  });
  const retire = useMutation({
    mutationFn: (artifactId: string) => pipelinesService.retireArtifact(artifactId),
    onSuccess: () => { setRetireId(null); refetchArtifacts(); queryClient.invalidateQueries({ queryKey: ['pipelines'] }); },
    onError: (e: any) => { setRetireId(null); toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Could not retire the artifact'), life: 10000 }); },
  });

  if (!isReady || !id || isLoading) return <PageSkeleton panels={2} />;
  if (!data) {
    return (
      <div className="p-4">
        <EmptyState
          icon="pi pi-search"
          title="This pipeline could not be found"
          hint="It may have been deleted, or the link points at another project."
          action={<span className="ui-press inline-flex"><Button label="Back to pipelines" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => navigateToList()} /></span>}
        />
      </div>
    );
  }

  const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted';
  const td = 'px-3 py-2.5 align-middle';

  return (
    <div className="p-4" data-testid="SCR-Pipeline-View">
      <Toast ref={toast} position="top-right" />

      {/* Identity line: what this recipe is, and the one action that runs it. */}
      <ScreenHeader
        icon="pi pi-sitemap"
        onBack={() => navigateToList()}
        title={data.name}
        right={
          <>
            <span className="font-mono text-xs text-ink-muted">{data.pipelineCode}</span>
            <StatusChip tone={data.status === 'ACTIVE' ? 'success' : 'neutral'}>{data.status}</StatusChip>
            <StatusChip tone="neutral" dot={false} className="tabular-nums">recipe v{data.version}</StatusChip>
            {canEdit && <span className="ui-press inline-flex"><Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary" outlined onClick={() => navigateToEdit(id)} /></span>}
            {canRun && data.status === 'ACTIVE' && (
              <span className="ui-press inline-flex">
                <Button label={run.isPending ? 'Starting…' : 'Run pipeline'} icon={run.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-play'} size="small"
                        disabled={run.isPending} onClick={() => run.mutate()} data-testid="Pipeline.Run" />
              </span>
            )}
          </>
        }
      />

      <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
        <TabPanel header="General">
          <div className="grid grid-cols-1 gap-3">
            {/* The recipe, grouped the way it runs: read, then split, then embed, then store. */}
            <Rise i={1} className={`${PANEL} p-4`}>
              <SectionLabel icon="pi pi-file-import">Sources &amp; parsing</SectionLabel>
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
                <Metric label="Supported types" value={(data.supportedSourceTypes || []).join(', ') || '—'} />
                <Metric label="Extract tables" value={yes(data.parserConfig?.extractTables)} />
                <Metric label="Strip headers / footers" value={`${yes(data.cleaningConfig?.removeHeaders)} / ${yes(data.cleaningConfig?.removeFooters)}`} />
                <Metric label="Keep page / file / section" value={`${yes(data.metadataConfig?.preservePageNumber)} / ${yes(data.metadataConfig?.preserveFileName)} / ${yes(data.metadataConfig?.preserveSection)}`} />
              </div>
            </Rise>

            <Rise i={2} className={`${PANEL} p-4`}>
              <SectionLabel icon="pi pi-database">Chunking, embedding &amp; storage</SectionLabel>
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-5">
                <Metric label="Strategy" value={data.chunkingConfig?.strategy || '—'} />
                <Metric label="Chunk / overlap" value={`${data.chunkingConfig?.chunkSize ?? '—'} / ${data.chunkingConfig?.chunkOverlap ?? '—'}`} />
                <Metric label="Embedding model" value={data.embeddingConfig?.modelId || data.embeddingConfig?.modelRef || '—'} hint={data.embeddingConfig?.modelId} />
                <Metric label="Dimensions" value={data.embeddingConfig?.dimensions ?? 'Learned from model'} />
                <Metric label="Vector store" value={`${data.outputConfig?.provider ?? '—'}${data.outputConfig?.indexName ? ` · ${data.outputConfig.indexName}` : ''}`} />
              </div>
            </Rise>

            {data.description && (
              <Rise i={3} className={`${PANEL} p-4`}>
                <SectionLabel icon="pi pi-align-left">Description</SectionLabel>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-700 dark:text-gray-300" style={PRETTY}>{data.description}</p>
              </Rise>
            )}

            <Rise i={4}>
              <AuditInfoCard surface="panel" createdAt={data.createdAt} updatedAt={data.updatedAt} createdBy={data.createdBy} updatedBy={data.updatedBy} />
            </Rise>
          </div>
        </TabPanel>

        <TabPanel header="Sources">
          <PipelineSourcesPanel pipelineId={id} supportedTypes={data.supportedSourceTypes || []} canEdit={canEdit} />
        </TabPanel>

        <TabPanel header={`Artifacts (${artifacts.length})`}>
          <Rise className={`${PANEL} overflow-hidden`}>
            {artifacts.length === 0 ? (
              <EmptyState icon="pi pi-box" title="No artifacts yet"
                          hint="Run the pipeline and it builds one. An artifact is what a RAG version binds to." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className={th}>Version</th><th className={th}>Code</th><th className={th}>Status</th>
                      <th className={th}>Model</th><th className={`${th} text-right`}>Dims</th>
                      <th className={`${th} text-right`}>Sources</th><th className={`${th} text-right`}>Chunks</th>
                      <th className={th}>Created</th><th className={`${th} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts.map((a) => (
                      <tr key={a._id} className="ui-row border-b border-gray-50 last:border-0 dark:border-gray-700">
                        <td className={`${td} font-mono tabular-nums text-ink`}>v{a.artifactVersion}</td>
                        <td className={`${td} font-mono text-xs text-gray-600 dark:text-gray-300`}>
                          {a.artifactCode}
                          {a.partial && <span className="ui-tone ui-tone--warn ml-1.5 font-sans" title="Built by a retry of some sources — not the whole corpus">partial</span>}
                        </td>
                        <td className={td}><StatusChip tone={artifactTone(a.status)}>{a.status}</StatusChip></td>
                        {/* One line with the full value on hover: a wrapped model
                            name pushed every row to three lines. */}
                        <td className={`${td} max-w-[180px] truncate text-gray-600 dark:text-gray-300`} title={a.embeddingModel}>{a.embeddingModel}</td>
                        <td className={`${td} text-right tabular-nums`}>{a.embeddingDimensions}</td>
                        <td className={`${td} text-right tabular-nums`}>{a.sourceCount}</td>
                        <td className={`${td} text-right tabular-nums`}>{a.chunkCount?.toLocaleString?.() ?? a.chunkCount}</td>
                        <td className={`${td} tabular-nums text-gray-600 dark:text-gray-300`}>{formatDate(a.createdAt)}</td>
                        <td className={`${td} whitespace-nowrap text-right`}>
                          {canRetire && a.status === 'READY' && (
                            <span className="ui-press inline-flex"><Button label="Retire" size="small" severity="danger" text onClick={() => setRetireId(a._id)} /></span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="border-t border-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-500 dark:border-gray-700 dark:text-gray-400" style={PRETTY}>
              An artifact is immutable. Retiring changes its status only; its chunks stay, so a RAG version can still roll back to it.
            </p>
          </Rise>
        </TabPanel>

        <TabPanel header="Runs">
          <PipelineRunList embedded pipelineId={id} />
        </TabPanel>
      </TabView>

      <DeleteConfirmDialog visible={!!retireId} onCancel={() => setRetireId(null)} onDelete={() => retireId && retire.mutate(retireId)}
                           message="Retire this artifact? It stops being offered to new RAG versions, and RAG versions can no longer serve from it. It is refused while a live or candidate RAG version uses it."
                           testId="Pipeline.Dialog.Retire" loading={retire.isPending}
                           header="Confirm retire" confirmLabel="Retire" loadingLabel="Retiring..."
                           confirmSeverity="warning" />
    </div>
  );
};

export default PipelineView;
